import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Checkbox, Chip, FormControl, IconButton, MenuItem, Paper, Select,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightlightRoundIcon from '@mui/icons-material/NightlightRound';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import { getCachevalue } from '../../../Utility/Services/CacheProviderService';
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../Components/Loader';
import { Colors } from '../../../Styles/Colors';
import dayjs from 'dayjs';
import { AppointmentCard, CommonPatientCard, CommonPatientDetails, ConsultantPatientCard } from '../../../Components/Common Components/CommonComponents';

const SESSION_OPTIONS = ['Morning', 'Afternoon', 'Evening'];
const DURATION_OPTIONS = [30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180];
const SESSION_DURATION_OPTIONS = [];
for (let m = 30; m <= 180; m += 15) SESSION_DURATION_OPTIONS.push(m);

const PREFERRED_SLOTS = [
    { value: 'Morning', label: 'Morning', range: '6 AM - 11 AM', icon: <WbSunnyIcon /> },
    { value: 'Afternoon', label: 'Afternoon', range: '12 PM - 4 PM', icon: <WbSunnyIcon /> },
    { value: 'Evening', label: 'Evening', range: '4 PM - 8 PM', icon: <NightlightRoundIcon /> },
];

const normalizeDate = (d) => {
    if (!d) return '';
    const str = typeof d === 'string' ? d : dayjs(d).format('YYYY-MM-DD');
    if (str.includes('-') && str.split('-')[0].length === 2) return dayjs(str, 'DD-MM-YYYY').format('YYYY-MM-DD');
    return dayjs(str).format('YYYY-MM-DD');
};

function TreatmentSlotConfig({ isFromIp = false }) {
    const getPatientData = useCallback(() => {
        try {
            const raw = getCachevalue('IppatientData')
            if (!raw) return {};
            return JSON.parse(raw);
        } catch (e) {
            return {};
        }
    }, [isFromIp]);

    const getApiIdParam = useCallback(() => {
        const p = getPatientData();
        if (isFromIp) return { ip_admission_id: p?.id ?? null };
        return { walkin_id: p?.id ?? p?.walkin_id ?? null };
    }, [isFromIp, getPatientData]);

    const patientData = getPatientData();
    const [treatment_date, setTreatment_date] = useState(
        patientData?.treatment_date ? dayjs(patientData.treatment_date).toDate() : new Date()
    );
    const [no_of_days, setNo_of_days] = useState(patientData?.no_of_days || patientData?.no_of_therapy_days);
    const [selectedSessions, setSelectedSessions] = useState({ Morning: false, Afternoon: false, Evening: false });
    const [sessionDurations, setSessionDurations] = useState({ Morning: 45, Afternoon: 30, Evening: 60 });
    const [slotList, setSlotList] = useState([]);
    const [showTableScreen, setShowTableScreen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [copySourceRow, setCopySourceRow] = useState(null);
    const [tableRows, setTableRows] = useState([]);
    const [therapyStartDate, setTherapyStartDate] = useState('');
    const [noOfTherapyDays, setNoOfTherapyDays] = useState(0);
    const [assignedDays, setAssignedDays] = useState(0);
    const [pendingDays, setPendingDays] = useState(0);
    const [progress, setProgress] = useState(0);
    const [masterView, setMasterView] = useState();
    const [isErrorMsg, setIsErrorMsg] = useState(false);
    const [isErrorMsgText, setIsErrorMsgText] = useState('');
    const [successMsg, setSuccessMsg] = useState(false);
    const [successMsgText, setSuccessMsgText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('All');

    const fetchSlots = useCallback(() => {
        setLoading(true);
        const param = getApiIdParam();
        const query = isFromIp
            ? `?ip_admission_id=${param.ip_admission_id || ''}`
            : `?walkin_id=${param.walkin_id || ''}`;
        RestAPIService.getAll(Serviceurls.AYURVEDA_TREATMENT_CREATE_SLOT + query)
            .then((res) => {
                if (res.data?.status === 'success' && res.data?.data?.length > 0) {
                    const data = res.data.data;
                    const sorted = [...data].sort((a, b) => new Date(a.treatment_date) - new Date(b.treatment_date));
                    const minDate = sorted[0]?.treatment_date;
                    const dates = [...new Set(sorted.map((d) => d.treatment_date))];
                    setSlotList(data);
                    setTableRows(sorted);
                    setShowTableScreen(true);
                    setTherapyStartDate(minDate || '');
                    setMasterView(true);
                    setNoOfTherapyDays(dates.length);
                    setAssignedDays(data.filter((d) => d.status && d.status !== 'Pending').length);
                    setPendingDays(data.filter((d) => !d.status || d.status === 'Pending').length);
                    setProgress(dates.length ? Math.round((data.length / dates.length) * 100) : 0);
                } else {
                    setMasterView(true);
                    setSlotList([]);
                    setShowTableScreen(false);
                }
            })
            .catch(() => {
                setSlotList([]);
                setShowTableScreen(false);
            })
            .finally(() => setLoading(false));
    }, [isFromIp, getApiIdParam]);

    useEffect(() => {
        fetchSlots();
    }, [fetchSlots]);

    const errorMessage = (msg) => {
        setIsErrorMsg(true);
        setIsErrorMsgText(msg);
    };
    const successMessage = (msg) => {
        setSuccessMsg(true);
        setSuccessMsgText(msg);
    };
    const msgClose = () => {
        setIsErrorMsg(false);
        setIsErrorMsgText('');
        setSuccessMsg(false);
        setSuccessMsgText('');
    };

    const handleCreateSlots = () => {
        const param = getApiIdParam();
        if (isFromIp && !param.ip_admission_id) {
            errorMessage('IP admission not found.');
            return;
        }
        if (!isFromIp && !param.walkin_id) {
            errorMessage('Walk-in admission not found.');
            return;
        }
        const slot = [];
        if (selectedSessions.Morning) slot.push({ session: 'Morning', duration: Number(sessionDurations.Morning) || 45, duration_mode: 'Minutes' });
        if (selectedSessions.Afternoon) slot.push({ session: 'Afternoon', duration: Number(sessionDurations.Afternoon) || 30, duration_mode: 'Minutes' });
        if (selectedSessions.Evening) slot.push({ session: 'Evening', duration: Number(sessionDurations.Evening) || 60, duration_mode: 'Minutes' });
        if (slot.length === 0) {
            errorMessage('Select at least one session (Morning, Afternoon or Evening).');
            return;
        }
        const payload = {
            ip_admission_id: isFromIp ? param.ip_admission_id : null,
            walkin_id: isFromIp ? null : param.walkin_id,
            treatment_date: dayjs(treatment_date).format('YYYY-MM-DD'),
            no_of_days: Number(no_of_days),
            slot,
        };
        setLoading(true);
        RestAPIService.create(payload, Serviceurls.AYURVEDA_TREATMENT_CREATE_SLOT)
            .then((res) => {
                if (res.data?.status === 'success') {
                    successMessage(res.data?.message || 'Sessions created.');
                    fetchSlots();
                } else {
                    setLoading(false);
                    errorMessage(res.data?.message || 'Failed to create sessions.');
                }
            })
            .catch((err) => {
                setLoading(false);
                errorMessage(err?.response?.data?.message || err?.message || 'Request failed.');
            });
    };

    const handleModifyItems = () => {
        if (!selectedIds.length) {
            errorMessage('Select at least one row to modify.');
            return;
        }
        const effectiveRows = copySourceRow && selectedIds.length
            ? tableRows.map((r) => (selectedIds.includes(r.id) ? { ...r, session: copySourceRow.session, duration: copySourceRow.duration } : r))
            : tableRows;
        const param = getApiIdParam();
        const session = selectedIds.map((id) => {
            const row = effectiveRows.find((r) => r.id === id);
            return {
                id: row.id,
                session: row.session || 'Morning',
                duration: Number(row.duration) || 45,
                duration_mode: 'Minutes',
                treatment_date: row.treatment_date ? dayjs(row.treatment_date).format('YYYY-MM-DD') : undefined,
            };
        });
        const payload = {
            ip_admission_id: isFromIp ? param.ip_admission_id : null,
            walkin_id: isFromIp ? null : param.walkin_id,
            session,
        };
        setLoading(true);
        setCopySourceRow(null);
        RestAPIService.updateWithOutId(payload, Serviceurls.AYURVEDA_TREATMENT_CREATE_SLOT)
            .then((res) => {
                if (res.data?.status === 'success') {
                    successMessage(res.data?.message || 'Sessions updated.');
                    setSelectedIds([]);
                    setCopySourceRow(null);
                    fetchSlots();
                } else {
                    setLoading(false);
                    errorMessage(res.data?.message || 'Update failed.');
                }
            })
            .catch((err) => {
                setLoading(false);
                errorMessage(err?.response?.data?.message || err?.message || 'Request failed.');
            });
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        const rowsToShow = selectedStatus === 'All' ? tableRows : tableRows.filter((r) => r.status === selectedStatus);
        if (selectedIds.length === rowsToShow.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(rowsToShow.map((r) => r.id));
        }
    };

    const copyFromRow = (row) => {
        setCopySourceRow(row);
    };

    const updateRowField = (id, field, value) => {
        setTableRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
        );
    };

    const handleDateChange = (rowId, newDate) => {
        const newDateStr = newDate ? dayjs(newDate).format('YYYY-MM-DD') : '';
        const existingDates = tableRows
            .filter((r) => r.id !== rowId)
            .map((r) => normalizeDate(r.treatment_date))
            .filter(Boolean);
        if (existingDates.includes(newDateStr)) {
            errorMessage('This date already exists in the session list. Choose a different date.');
            return;
        }
        updateRowField(rowId, 'treatment_date', newDateStr);
    };

    const clearAllSessions = () => {
        setSelectedSessions({ Morning: false, Afternoon: false, Evening: false });
        setSessionDurations({ Morning: 60, Afternoon: 60, Evening: 60 });
        setTableRows([]);
    };

    const onCardClick = (status) => {
        setSelectedStatus(status);
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            Completed: { color: '#43C64F', bgColor: '#ECFFE9' },
            'Assign Treatment': { color: '#F1584A', bgColor: '#FFE9E9' },
            Scheduled: { color: '#29B6F6', bgColor: '#E3F2FD' },
            'Not Scheduled': { color: '#FF7723', bgColor: '#FFF3E0' },
            Cancelled: { color: '#9E9E9E', bgColor: '#F5F5F5' },
        };
        const config = statusConfig[status] || statusConfig['Not Scheduled'];
        return (
            <Chip
                label={status}
                size="small"
                sx={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                }}
            />
        );
    };

    const filteredRows = selectedStatus === 'All'
        ? tableRows
        : tableRows.filter((r) => r.status === selectedStatus);

    let attender = {
        lineOne: "AttenderName",
        lineTwo: `${patientData?.attender_name ? patientData?.attender_name : '-'}`,
        lineFour: 'ContactNumber',
        LineFive: `${patientData?.attender_mobile ? patientData?.attender_mobile : '-'}`
    }

    let consultant = {
        lineOne: "ConsultantName",
        lineTwo: `${patientData?.doctor_name ? patientData?.doctor_name : '-'}`,
    }

    const renderFirstScreen = () => {
        const hasAnySession = selectedSessions.Morning || selectedSessions.Afternoon || selectedSessions.Evening;
        return (
            <Box display="flex" flexDirection="column" gap={2}>
                <Box display={'flex'} alignItems='center' emed_tid="surgery_notes_header_content">
                    <Box className='eMed_Patient_Details_Long eMed_patient_box' emed_tid="surgery_notes_patient_details">
                        <CommonPatientDetails data={patientData} showDetailed={true} emed_tid="surgery_notes_patient_details_component" />
                    </Box>
                    <CommonPatientCard details={attender} showDetailed={true} emed_tid="surgery_notes_attender_card" />
                    <ConsultantPatientCard name={consultant} emed_tid="surgery_notes_consultant_card" />
                </Box>
                <Paper sx={{ p: 2, maxWidth: 520, mx: 'auto' }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        Add Sessions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Auto-generate a series of Ayurveda therapy appointments for your patient.
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            disabled={true}
                            label="Start Date"
                            value={treatment_date ? dayjs(treatment_date) : null}
                            onChange={(d) => setTreatment_date(d ? d.toDate() : new Date())}
                            renderInput={(params) => <TextField {...params} size="small" fullWidth sx={{ mb: 2 }} />}
                        />
                    </LocalizationProvider>
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Number of Days"
                        value={no_of_days}
                        disabled={true}
                        onChange={(e) => setNo_of_days(e.target.value)}
                        inputProps={{ min: 1 }}
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Preferred Slot
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                        {PREFERRED_SLOTS.map((s) => (
                            <Button
                                key={s.value}
                                variant={selectedSessions[s.value] ? 'contained' : 'outlined'}
                                size="medium"
                                startIcon={s.icon}
                                onClick={() =>
                                    setSelectedSessions((prev) => ({
                                        ...prev,
                                        [s.value]: !prev[s.value],
                                    }))
                                }
                                sx={{ flex: 1, minWidth: 120, textTransform: 'none' }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography variant="body2" fontWeight={600}>{s.label}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{s.range}</Typography>
                                </Box>
                            </Button>
                        ))}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Duration
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {PREFERRED_SLOTS.map((s) => (
                            <FormControl key={s.value} size="small" sx={{ minWidth: 120, flex: 1 }}>
                                <Select
                                    value={sessionDurations[s.value] ?? 60}
                                    onChange={(e) =>
                                        setSessionDurations((prev) => ({
                                            ...prev,
                                            [s.value]: Number(e.target.value),
                                        }))
                                    }
                                    displayEmpty
                                    sx={{ borderRadius: 1 }}
                                >
                                    {SESSION_DURATION_OPTIONS.map((m) => (
                                        <MenuItem key={m} value={m}>{m} mins</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        <Button variant="outlined" onClick={clearAllSessions}>
                            Clear All
                        </Button>
                        <Button variant="contained" disabled={!hasAnySession} onClick={handleCreateSlots}>
                            Apply
                        </Button>
                    </Box>
                </Paper>
            </Box>
        )
    };

    const renderSecondScreen = () => (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Box className="eMed_Patient_Details_Long eMed_patient_box" minWidth={200}>
                    <CommonPatientDetails data={patientData} showDetailed={true} />
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                    <AppointmentCard
                        onClick={() => onCardClick('All')}
                        count={tableRows.length || 0}
                        label="All"
                        color="#04B7B1"
                        isSelectedCard={selectedStatus === 'All'}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick('Assign Treatment')}
                        count={tableRows.filter((r) => r.status === 'Assign Treatment').length || 0}
                        label="Assign Treatment"
                        color="#616161"
                        isSelectedCard={selectedStatus === 'Assign Treatment'}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick('Not Scheduled')}
                        count={tableRows.filter((r) => r.status === 'Not Scheduled').length || 0}
                        label="Not Scheduled"
                        color="orange"
                        isSelectedCard={selectedStatus === 'Not Scheduled'}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick('Scheduled')}
                        count={tableRows.filter((r) => r.status === 'Scheduled').length || 0}
                        label="Scheduled"
                        color="#0461B7"
                        isSelectedCard={selectedStatus === 'Scheduled'}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick('Completed')}
                        count={tableRows.filter((r) => r.status === 'Completed').length || 0}
                        label="Completed"
                        color="green"
                        isSelectedCard={selectedStatus === 'Completed'}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick('Cancelled')}
                        count={tableRows.filter((r) => r.status === 'Cancelled').length || 0}
                        label="Cancelled"
                        color="red"
                        isSelectedCard={selectedStatus === 'Cancelled'}
                    />
                </Box>
            </Box>
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                        Session Table
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button variant="contained" size="small" onClick={handleModifyItems}>
                            MODIFY ITEMS ({selectedIds.length})
                        </Button>
                        <Button variant="contained" size="small" onClick={handleModifyItems}>
                            Save
                        </Button>
                    </Box>
                </Box>
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={filteredRows.length > 0 && selectedIds.length === filteredRows.length}
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredRows.length}
                                        onChange={toggleSelectAll}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: Colors.DataTblHeaderbg }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: Colors.DataTblHeaderbg }}>Session</TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: Colors.DataTblHeaderbg }}>Duration</TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: Colors.DataTblHeaderbg }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: Colors.DataTblHeaderbg }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedIds.includes(row.id)}
                                            onChange={() => toggleSelect(row.id)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                value={row.treatment_date ? dayjs(row.treatment_date) : null}
                                                onChange={(newDate) => handleDateChange(row.id, newDate)}
                                                renderInput={(params) => (
                                                    <TextField {...params} size="small" fullWidth />
                                                )}
                                                format="DD-MM-YYYY"
                                            />
                                        </LocalizationProvider>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={row.session || 'Morning'}
                                                onChange={(e) => updateRowField(row.id, 'session', e.target.value)}
                                            >
                                                {SESSION_OPTIONS.map((s) => (
                                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={Number(row.duration) || 60}
                                                onChange={(e) => updateRowField(row.id, 'duration', e.target.value)}
                                            >
                                                {DURATION_OPTIONS.map((d) => (
                                                    <MenuItem key={d} value={d}>{d}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>{getStatusChip(row.status) || '-'}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            title="Copy to selected"
                                            onClick={() => copyFromRow(row)}
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );

    return (
        <Box sx={{ p: 2 }}>
            {masterView ? (!showTableScreen ? renderFirstScreen() : renderSecondScreen()) : null}
            {loading && <Loader loaderOpen={true} />}
            {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
            {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
        </Box>
    );
}

export default TreatmentSlotConfig;
